import { MeetingTypeDto } from '@shared/interfaces/dtos/global';
import { Component, effect, inject, model, OnDestroy, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MeetingsService } from '@shared/services/meetings.service';
import { MeetingSearchFormGroup } from '@shared/types/points';
import { finalize, Subscription } from 'rxjs';
import { Meeting } from '@shared/interfaces/meetings';
import { PointsAdvancedSearchService } from '../services/points-advanced-search.service';
import { extractElementsFromQueryParam } from '@shared/helpers';

@Component({
  selector: 'idel-meeting-search-for-points',
  templateUrl: './meeting-search-for-points.component.html',
  styleUrl: './meeting-search-for-points.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class MeetingSearchForPointsComponent implements OnInit, OnDestroy {
  private readonly pointsAdvancedSearchService = inject(PointsAdvancedSearchService);
  private readonly service = inject(MeetingsService);

  public readonly form = model.required<FormGroup<MeetingSearchFormGroup>>();

  public readonly fetchingMeetingTypes = signal<boolean>(false);
  public readonly fetchingMeetings = signal<boolean>(false);
  public readonly meetingTypesOptions = signal<MeetingTypeDto[]>([]);
  public readonly meetingsOptions = signal<Meeting[]>([]);
  public readonly canSelectMeetings = signal<boolean>(false);
  public readonly dateFormat = signal<'single' | 'range'>('single');

  private readonly shouldPopulate = this.pointsAdvancedSearchService.shouldPopulateForms;
  private readonly shouldPopulateEff = effect(
    () => {
      const shouldPopulate = this.shouldPopulate();

      if (shouldPopulate) {
        this.populateFromCurrentSearch();
      }
    },
    { allowSignalWrites: true }
  );

  private sub: Subscription | null = null;

  public get meetingsPlaceholder(): string {
    const selectedMeetingTypes = this.form().value.meetingTypes;

    if (!selectedMeetingTypes || !selectedMeetingTypes.length) return 'Sélectionner un type de séance';

    return 'Sélectionner une séance';
  }

  ngOnInit(): void {
    this.form().controls.meetings.disable();
    this.initFormSubscription();
    this.fetchMeetingTypes();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.shouldPopulateEff.destroy();
  }

  private populateFromCurrentSearch(): void {
    const currentSearch = this.pointsAdvancedSearchService.currentSearch();

    if (!currentSearch) return;

    this.pointsAdvancedSearchService.setFormPopulationProgressAsStartedFor('meeting');

    const selectedMeetingTypes = this.meetingTypesOptions().filter((type) => {
      const typeIds = extractElementsFromQueryParam(currentSearch?.TypeSeanceIds);

      if (!typeIds.length) return false;

      return typeIds.includes(type.id.toString());
    });

    if (Array.isArray(currentSearch.Date) || (currentSearch.DateDebut && currentSearch.DateFin)) {
      this.dateFormat.set('range');
    }

    const selectedDate: Date | [Date, Date] | null = currentSearch.Date
      ? Array.isArray(currentSearch.Date)
        ? [new Date(currentSearch.Date[0]), new Date(currentSearch.Date[1])]
        : new Date(currentSearch.Date)
      : currentSearch.DateDebut && currentSearch.DateFin
        ? [new Date(currentSearch.DateDebut), new Date(currentSearch.DateFin)]
        : currentSearch.DateDebut
          ? new Date(currentSearch.DateDebut)
          : null;

    this.form().patchValue({
      meetingTypes: selectedMeetingTypes ?? [],
      // meetings will be populated when fetchMeetingsByTypes is called in subscription
      date: selectedDate ?? null,
    });

    if (!currentSearch?.SeanceIds?.length) {
      this.pointsAdvancedSearchService.setFormPopulationProgressAsDoneFor('meeting');
    }
  }

  private initFormSubscription(): void {
    this.sub = this.form().controls.meetingTypes.valueChanges.subscribe((selectedMeetingTypes) => {
      this.canSelectMeetings.set(selectedMeetingTypes?.length > 0);

      if (this.canSelectMeetings()) {
        this.form().controls.meetings.enable();
        this.fetchMeetingsByTypes(selectedMeetingTypes);
        return;
      }

      this.form().controls.meetings.disable();
    });
  }

  private fetchMeetingTypes(): void {
    this.fetchingMeetingTypes.set(true);

    this.service
      .fetchMeetingTypes()
      .pipe(finalize(() => this.fetchingMeetingTypes.set(false)))
      .subscribe({
        next: (meetingTypes) => {
          this.meetingTypesOptions.set(meetingTypes);
        },
      });
  }

  private fetchMeetingsByTypes(types: MeetingTypeDto[]): void {
    this.fetchingMeetings.set(true);

    this.service
      .fetchMeetings({ TypeSeanceIds: types.map((type) => type.id) })
      .pipe(finalize(() => this.fetchingMeetings.set(false)))
      .subscribe({
        next: (meetings) => {
          this.meetingsOptions.set(meetings);

          if (this.shouldPopulate()) {
            const selectedMeetings = this.meetingsOptions().filter((meeting) => {
              const meetingIds = extractElementsFromQueryParam(
                this.pointsAdvancedSearchService.currentSearch()?.SeanceIds
              );
              if (!meetingIds.length) return false;

              return meetingIds.includes(meeting.id.toString());
            });

            this.form().patchValue({
              meetings: selectedMeetings ?? [],
            });

            this.pointsAdvancedSearchService.setFormPopulationProgressAsDoneFor('meeting');
          }
        },
      });
  }

  public switchDateFormat(): void {
    this.dateFormat.set(this.dateFormat() === 'single' ? 'range' : 'single');
    this.form().controls.date.patchValue(null);
  }
}
